// @ts-nocheck
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMyOrganizations } from "@/contexts/MyOrganizationsContext";

export interface MyOrganization {
  id: string;
  name: string;
  logo_url: string | null;
}

/**
 * Resolves the signed-in user's *active* organization.
 *
 * When rendered inside a `MyOrganizationsProvider` (the org-admin shell), this
 * returns the org currently selected in the org switcher, so switching orgs
 * re-scopes the whole dashboard. Outside that provider it falls back to a
 * standalone fetch of the user's first active membership.
 */
export function useMyOrganization() {
  const ctx = useMyOrganizations();
  const { user } = useAuth();
  const [organization, setOrganization] = useState<MyOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // When a provider is present, it owns org resolution — skip the fallback.
    if (ctx) return;

    if (!user?.id) {
      setOrganization(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const { data } = await (supabase as any)
        .from("organization_members")
        .select("organization_id, organizations:organization_id(id, name, logo_url)")
        .eq("user_id", user.id)
        .eq("status", "active")
        .limit(1)
        .maybeSingle();

      if (!cancelled) {
        const org = data?.organizations || null;
        setOrganization(
          org ? { id: org.id, name: org.name, logo_url: org.logo_url } : null
        );
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, !!ctx]);

  if (ctx) {
    const org = ctx.activeOrganization;
    return {
      organization: org
        ? { id: org.id, name: org.name, logo_url: org.logo_url }
        : null,
      isLoading: ctx.isLoading,
    };
  }

  return { organization, isLoading };
}
